import React, { useState, useEffect } from 'react';
import { Card, Button, Drawer, Descriptions, Tag, Row, Col, Alert, Space } from 'antd';
import AntTable from '../../components/CustomTable/AntTable';
import { getFraudDecisions, getFraudDecisionDetail, getFraudDecisionsSummary } from '../../services/fraudCheckService';
import dayjs from 'dayjs';

const DecisionsHistoryTab = () => {
  const [loading, setLoading] = useState(false);
  const [decisions, setDecisions] = useState([]);
  const [error, setError] = useState(null);
  
  // Stats summary state
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    rejected: 0,
  });

  // Drawer/Modal for detail view
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchSummary = async () => {
    try {
      const res = await getFraudDecisionsSummary();
      // Handle different formats from summary endpoint
      // Example 1: { success: true, data: { total: 10, approved: 6, rejected: 4 } }
      // Example 2: { success: true, data: [{ action: 'APPROVE', count: 6 }, { action: 'REJECT', count: 4 }] }
      const data = res?.data || res;
      
      let total = 0;
      let approved = 0;
      let rejected = 0;

      if (data && typeof data === 'object') {
        const byAction = data.by_action || data.byAction || {};
        
        // Sum counts from by_action object keys
        for (const [key, val] of Object.entries(byAction)) {
          const uKey = key.toUpperCase();
          const count = Number(val || 0);
          if (uKey.includes('APPROVE') || uKey.includes('ALLOW') || uKey.includes('PASS')) {
            approved += count;
          } else if (uKey.includes('REJECT') || uKey.includes('DENY') || uKey.includes('BLOCK')) {
            rejected += count;
          }
          total += count;
        }

        // Fallback to flat root properties if by_action is empty
        if (total === 0) {
          if (Array.isArray(data)) {
            data.forEach(item => {
              const act = String(item.action || item.decision || '').toUpperCase();
              const count = Number(item.count || item.total || 0);
              if (act.includes('APPROVE') || act.includes('ALLOW') || act.includes('PASS')) {
                approved = count;
              } else if (act.includes('REJECT') || act.includes('DENY') || act.includes('BLOCK')) {
                rejected = count;
              }
              total += count;
            });
          } else {
            approved = Number(data.approved || data.approvedCount || data.approve || 0);
            rejected = Number(data.rejected || data.rejectedCount || data.reject || 0);
            total = Number(data.total || data.totalCount || (approved + rejected));
          }
        }
      }
      setStats({ total, approved, rejected });
    } catch (err) {
      console.error('Error fetching decisions summary:', err);
    }
  };

  const fetchDecisions = async (page = 1, size = 10) => {
    setLoading(true);
    setError(null);
    try {
      // API page index is 0-indexed
      const res = await getFraudDecisions({ page: page - 1, size });
      
      // Page response structure typical Spring Boot:
      // res.data = { content: [...], totalElements: 12 }
      // Or fallback: direct array
      const content = res?.data?.content || res?.content || res?.data || (Array.isArray(res) ? res : []);
      const totalElements = res?.data?.totalElements || res?.totalElements || res?.total || content.length;

      // Inject standard 'id' for AntTable's rowKey
      const formatted = content.map((item, index) => ({
        ...item,
        id: item.id || item.reference || `decision_${index}`,
      }));

      setDecisions(formatted);
      setPagination({
        current: page,
        pageSize: size,
        total: totalElements,
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch decisions history.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchDecisions(1, 10);
  }, []);

  const handleTableChange = ({ pagination: newPagination }) => {
    fetchDecisions(newPagination.current, newPagination.pageSize);
  };

  const handleInspect = async (record) => {
    setDetailLoading(true);
    setDrawerVisible(true);
    try {
      if (record.id) {
        const detailRes = await getFraudDecisionDetail(record.id);
        if (detailRes && (detailRes.data || detailRes.success)) {
          setSelectedDecision(detailRes.data || detailRes);
        } else {
          setSelectedDecision(record);
        }
      } else {
        setSelectedDecision(record);
      }
    } catch (err) {
      console.error('Error fetching decision details, falling back to table record:', err);
      setSelectedDecision(record);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      title: 'Decision ID',
      dataIndex: 'id',
      key: 'id',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'User / Account Details',
      key: 'userDetails',
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div><strong>User ID:</strong> {record.userId || record.checkerEmail || 'N/A'}</div>
          {record.fromAccount && <div style={{ fontSize: '11px', color: '#8c8c8c' }}>From: {record.fromAccount}</div>}
          {record.toAccount && <div style={{ fontSize: '11px', color: '#8c8c8c' }}>To: {record.toAccount}</div>}
        </div>
      )
    },
    {
      title: 'Transaction Details',
      key: 'transactionDetails',
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          {record.amount !== undefined ? (
            <div><strong>NGN {record.amount.toLocaleString()}</strong></div>
          ) : (
            <div style={{ color: '#bfbfbf' }}>N/A</div>
          )}
          {record.channel && <div style={{ fontSize: '11px', color: '#8c8c8c' }}>{record.channel}</div>}
        </div>
      )
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
    },
    {
      title: 'Decision / Score',
      key: 'decisionScore',
      render: (_, record) => {
        const val = String(record.action || record.actionTaken || '').toUpperCase();
        let color = 'default';
        if (val.includes('ALLOW') || val.includes('APPROVE')) {
          color = 'success';
        } else if (val.includes('DENY') || val.includes('BLOCK') || val.includes('REJECT')) {
          color = 'error';
        }
        return (
          <div>
            <Tag color={color}>{val || 'N/A'}</Tag>
            {record.score !== undefined && (
              <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: 2 }}>
                Score: {record.score}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Reason / Rules',
      key: 'reasonRules',
      render: (_, record) => {
        let rulesCount = 0;
        try {
          if (record.rulesFiredJson) {
            const rules = JSON.parse(record.rulesFiredJson);
            rulesCount = Array.isArray(rules) ? rules.length : 0;
          }
        } catch (e) {}

        return (
          <div style={{ fontSize: '12px' }}>
            <div>{record.comment || record.reasonCode || 'N/A'}</div>
            {rulesCount > 0 && (
              <Tag color="warning" style={{ fontSize: '10px', marginTop: 4 }}>
                {rulesCount} Rules Fired
              </Tag>
            )}
          </div>
        );
      }
    },
    {
      title: 'Decided Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'N/A'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button type="default" size="small" onClick={() => handleInspect(record)}>
          Details
        </Button>
      ),
    },
  ];

  return (
    <div>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      {/* Stats Summary Cards */}
      <div className="stats-container">
        <div className="stat-card total">
          <div className="stat-card-content">
            <span className="stat-card-title">Total Decisions</span>
            <span className="stat-card-value">{stats.total}</span>
          </div>
        </div>
        <div className="stat-card approved">
          <div className="stat-card-content">
            <span className="stat-card-title">Approved Actions</span>
            <span className="stat-card-value">{stats.approved}</span>
          </div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-card-content">
            <span className="stat-card-title">Rejected Actions</span>
            <span className="stat-card-value">{stats.rejected}</span>
          </div>
        </div>
      </div>

      <div className="fraud-toolbar">
        <h3 style={{ margin: 0, fontWeight: 600, color: '#262626' }}>History Log</h3>
        <Button type="default" onClick={() => { fetchSummary(); fetchDecisions(pagination.current, pagination.pageSize); }}>
          Refresh
        </Button>
      </div>

      <AntTable
        loading={loading}
        users={decisions}
        pagination={pagination}
        handleGetNext={handleTableChange}
        columns={columns}
      />

      <Drawer
        title={`Decision Detail: ${selectedDecision?.id || ''}`}
        placement="right"
        width={650}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        className="fraud-detail-drawer"
      >
        {detailLoading ? (
          <div>Loading details...</div>
        ) : selectedDecision ? (
          <div className="fraud-detail-container">
            <div className="fraud-detail-section">
              <h4>Decision Context</h4>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Decision ID">{selectedDecision.id}</Descriptions.Item>
                <Descriptions.Item label="Reference">{selectedDecision.reference || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="User ID">{selectedDecision.userId || 'N/A'}</Descriptions.Item>
                {selectedDecision.fromAccount && (
                  <Descriptions.Item label="From Account">{selectedDecision.fromAccount}</Descriptions.Item>
                )}
                {selectedDecision.toAccount && (
                  <Descriptions.Item label="To Account">{selectedDecision.toAccount}</Descriptions.Item>
                )}
                {selectedDecision.amount !== undefined && (
                  <Descriptions.Item label="Amount">NGN {selectedDecision.amount.toLocaleString()}</Descriptions.Item>
                )}
                {selectedDecision.channel && (
                  <Descriptions.Item label="Channel">{selectedDecision.channel}</Descriptions.Item>
                )}
                <Descriptions.Item label="Decision">
                  <Tag color={String(selectedDecision.action || selectedDecision.actionTaken || '').toUpperCase().includes('ALLOW') || String(selectedDecision.action || selectedDecision.actionTaken || '').toUpperCase().includes('APPROVE') ? 'green' : 'red'}>
                    {selectedActionTaken(selectedDecision.action || selectedDecision.actionTaken)}
                  </Tag>
                </Descriptions.Item>
                {selectedDecision.score !== undefined && (
                  <Descriptions.Item label="Fraud Score">{selectedDecision.score}</Descriptions.Item>
                )}
                <Descriptions.Item label="Checker / Email">
                  {selectedDecision.checkerEmail || selectedDecision.userId || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Decided At">
                  {selectedDecision.createdAt ? dayjs(selectedDecision.createdAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </div>

            <div className="fraud-detail-section">
              <h4>Comments & Remarks</h4>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Reason / Comment">{selectedDecision.comment || selectedDecision.reasonCode || 'N/A'}</Descriptions.Item>
                {selectedDecision.reasonCode && (
                  <Descriptions.Item label="Reason Code">{selectedDecision.reasonCode}</Descriptions.Item>
                )}
                {selectedDecision.makerEmail && (
                  <Descriptions.Item label="Original Maker">{selectedDecision.makerEmail}</Descriptions.Item>
                )}
                {selectedDecision.rulesFiredJson && selectedDecision.rulesFiredJson !== "[]" && (
                  <Descriptions.Item label="Rules Fired">
                    <pre style={{ fontSize: '11px', margin: 0, whiteSpace: 'pre-wrap' }}>
                      {(() => {
                        try {
                          const parsed = JSON.parse(selectedDecision.rulesFiredJson);
                          return JSON.stringify(parsed, null, 2);
                        } catch (e) {
                          return String(selectedDecision.rulesFiredJson);
                        }
                      })()}
                    </pre>
                  </Descriptions.Item>
                )}
                {selectedDecision.metadata && (
                  <Descriptions.Item label="Transaction Details">
                    <pre style={{ fontSize: '11px', margin: 0 }}>
                      {typeof selectedDecision.metadata === 'object' 
                        ? JSON.stringify(selectedDecision.metadata, null, 2) 
                        : String(selectedDecision.metadata)}
                    </pre>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          </div>
        ) : (
          <div>No record selected</div>
        )}
      </Drawer>
    </div>
  );
};

// Helper for formatting action status
const selectedActionTaken = (action) => {
  const val = String(action || '').toUpperCase();
  if (val.includes('ALLOW') || val.includes('APPROVE')) return 'ALLOW / APPROVED';
  if (val.includes('DENY') || val.includes('BLOCK') || val.includes('REJECT')) return 'DENY / REJECTED';
  return val || 'N/A';
};

export default DecisionsHistoryTab;
